import React, { useEffect, useMemo, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { html } from "@codemirror/lang-html";
import { dracula } from "@uiw/codemirror-theme-dracula";
import { toast } from "sonner";
import { Mail, Play, Loader2, AlertTriangle, Download, Sparkles, Eye, Code2 } from "lucide-react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const DEFAULT_CODE = `<div style="display: flex; justify-content: space-between; padding: 16px; background: #111827;">
  <span style="color: #fff; font-weight: bold;">Orbit Retail</span>
  <span style="color: #2dd4bf;">Weekend Sale</span>
</div>
<h1 style="padding: 0 16px;">20% off everything this weekend</h1>`;

const RISK_CHECKS = [
  { pattern: /display\s*:\s*flex/i, message: "Flexbox layout detected — most Outlook desktop versions ignore flexbox; it will be converted to a table-based layout." },
  { pattern: /display\s*:\s*grid/i, message: "CSS grid detected — not supported in Outlook; will be converted to tables." },
  { pattern: /<link[^>]*stylesheet/i, message: "External stylesheet detected — most email clients strip <link> tags; critical styles need to be inlined." },
  { pattern: /position\s*:\s*(fixed|absolute)/i, message: "position: fixed/absolute is unreliable across email clients — avoid for layout." },
  { pattern: /<video/i, message: "<video> tags aren't supported by most email clients — consider a linked image/GIF fallback." },
  { pattern: /<script/i, message: "<script> tags are stripped entirely by all major email clients." },
  { pattern: /<form/i, message: "<form> elements are not supported inside most email clients." },
  { pattern: /@font-face/i, message: "Custom web fonts (@font-face) have inconsistent support — provide a safe fallback font stack." },
];

function getWarnings(code) {
  return RISK_CHECKS.filter((c) => c.pattern.test(code)).map((c) => c.message);
}

export default function EmailCompilerPage() {
  const [code, setCode] = useState(DEFAULT_CODE);
  const [templates, setTemplates] = useState([]);
  const [compiling, setCompiling] = useState(false);
  const [compileResult, setCompileResult] = useState(null);
  const [previewTab, setPreviewTab] = useState("preview");
  const [sidebarTab, setSidebarTab] = useState("templates");

  useEffect(() => {
    api.get("/email-compiler/templates").then((r) => setTemplates(r.data));
  }, []);

  const warnings = useMemo(() => getWarnings(code), [code]);

  const compile = async () => {
    setCompiling(true);
    try {
      const { data } = await api.post("/email-compiler/compile", { html: code });
      setCompileResult(data);
      setPreviewTab("compiled");
      toast.success("Compiled to email-safe HTML");
    } catch (e) {
      toast.error("Failed to compile template");
    } finally {
      setCompiling(false);
    }
  };

  const downloadCompiled = () => {
    const blob = new Blob([compileResult.compiled_html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "email-template.html";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="email-compiler-page">
      <div className="flex items-center gap-3 mb-2">
        <Mail className="w-6 h-6 text-teal-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400">SFMC Track</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">Email-HTML Template Compiler</h1>
      <p className="text-sm text-slate-400 mt-2 max-w-2xl">
        Write modern HTML/CSS, then compile it into email-client-safe HTML (table-based layout, inlined styles) —
        the same transformation real SFMC email developers do by hand for Outlook/Gmail compatibility.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="glass-panel rounded-2xl p-1 overflow-hidden border border-teal-400/10" data-testid="email-compiler-editor">
            <CodeMirror value={code} height="280px" theme={dracula} extensions={[html()]} onChange={(val) => setCode(val)} />
          </div>

          {warnings.length > 0 && (
            <div className="glass-panel rounded-2xl p-4 border border-amber-400/20" data-testid="email-compiler-warnings-panel">
              <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-amber-400 mb-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Compatibility Warnings ({warnings.length})
              </p>
              <ul className="space-y-1.5">
                {warnings.map((w, i) => (
                  <li key={`${i}-${w}`} className="text-xs text-amber-200/80 bg-amber-400/5 border border-amber-400/10 rounded-lg p-2.5" data-testid={`email-compiler-warning-${i}`}>
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={compile}
              disabled={compiling}
              data-testid="email-compiler-compile-btn"
              className="flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-slate-950 text-sm font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60 transition-colors"
            >
              {compiling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Compile to Email-Safe HTML
            </button>
          </div>

          <div className="glass-panel rounded-2xl p-4 border border-white/5">
            <Tabs value={previewTab} onValueChange={setPreviewTab}>
              <div className="flex items-center justify-between mb-3">
                <TabsList data-testid="email-compiler-preview-tabs">
                  <TabsTrigger value="preview" data-testid="email-compiler-tab-preview"><Eye className="w-3.5 h-3.5 mr-1" /> Live Preview</TabsTrigger>
                  <TabsTrigger value="compiled" data-testid="email-compiler-tab-compiled" disabled={!compileResult}><Code2 className="w-3.5 h-3.5 mr-1" /> Compiled</TabsTrigger>
                </TabsList>
                {compileResult && previewTab === "compiled" && (
                  <button onClick={downloadCompiled} data-testid="email-compiler-download-btn" className="flex items-center gap-1.5 text-xs font-semibold text-teal-300 hover:text-teal-200">
                    <Download className="w-3.5 h-3.5" /> Download HTML
                  </button>
                )}
              </div>
              <TabsContent value="preview">
                <iframe title="Live preview" srcDoc={code} className="w-full h-72 rounded-lg bg-white" data-testid="email-compiler-live-preview" />
              </TabsContent>
              <TabsContent value="compiled">
                {compileResult && (
                  <>
                    <iframe title="Compiled preview" srcDoc={compileResult.compiled_html} className="w-full h-72 rounded-lg bg-white mb-4" data-testid="email-compiler-compiled-preview" />
                    {compileResult.compatibility_notes?.length > 0 && (
                      <div data-testid="email-compiler-compatibility-notes">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-teal-400 mb-2">What Was Fixed</p>
                        <ul className="text-xs text-slate-300 space-y-1 list-disc pl-4">
                          {compileResult.compatibility_notes.map((n, i) => <li key={`${i}-${n}`}>{n}</li>)}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <div className="glass-panel rounded-2xl p-4">
          <Tabs value={sidebarTab} onValueChange={setSidebarTab}>
            <TabsList data-testid="email-compiler-sidebar-tabs" className="w-full grid grid-cols-1">
              <TabsTrigger value="templates" data-testid="email-compiler-sidebar-tab-templates">Starter Templates</TabsTrigger>
            </TabsList>
            <TabsContent value="templates" className="mt-3 space-y-2">
              {templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => { setCode(t.code); setCompileResult(null); setPreviewTab("preview"); }}
                  data-testid={`email-compiler-template-${t.id}`}
                  className="w-full text-left text-xs bg-white/[0.02] hover:bg-teal-400/5 border border-white/5 hover:border-teal-400/20 rounded-lg p-3 transition-colors"
                >
                  <p className="text-slate-200 font-semibold">{t.title}</p>
                  <p className="text-slate-400 mt-1">{t.description}</p>
                </button>
              ))}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <Play className="w-3.5 h-3.5 text-slate-500" />
        <p className="text-xs text-slate-500">Tip: pick a starter template with intentional compatibility issues, then compile to see exactly what gets fixed.</p>
      </div>
    </div>
  );
}
